import { useEffect, useState } from 'react';
import { useLocation as useLocationLegacy } from '@tanstack/react-location';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { shell } from '@frontend/shell-utils';
import { useLocation, useNavigate, ParsedLocation } from '@frontend/weave-router';

/**
 * a naive wrapper of the window history stack of pages within the weave app
 * Allows to record the history of pages visited within the app and navigate between them
 */

export const useHistoryControl = () => {
  let legacyLoc: ReturnType<typeof useLocationLegacy> | undefined;
  try {
    // This is not actually conditionally called
    // eslint-disable-next-line react-hooks/rules-of-hooks
    legacyLoc = useLocationLegacy();
  } catch {
    console.error('useLocation is not available in this context');
    legacyLoc = undefined;
  }

  let loc: ParsedLocation | undefined;
  try {
    // This is not actually conditionally called
    // eslint-disable-next-line react-hooks/rules-of-hooks
    loc = useLocation();
  } catch {
    console.error('useLocation is not available in this context');
    loc = undefined;
  }

  let navigate: ReturnType<typeof useNavigate> | undefined;
  try {
    // This is not actually conditionally called
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigate = useNavigate();
  } catch {
    console.error('useNavigate is not available in this context');
    navigate = undefined;
  }

  let settingsNavigate: ReturnType<typeof useSettingsNavigate> | undefined;
  try {
    // This is not actually conditionally called
    // eslint-disable-next-line react-hooks/rules-of-hooks
    settingsNavigate = useSettingsNavigate();
  } catch {
    console.error('useSettingsNavigate is not available in this context');
    settingsNavigate = undefined;
  }

  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);

  const back = () => {
    if (shell.isShell && (navigate || settingsNavigate)) {
      shell.invoke?.('window:history:back', undefined);
    } else {
      history.back();
    }
  };
  const forward = () => {
    if (shell.isShell && (navigate || settingsNavigate)) {
      shell.invoke?.('window:history:forward', undefined);
    } else {
      history.forward();
    }
  };

  useEffect(() => {
    if (!shell) return;
    //race condition here. this history stack is updated after a 'push' event from another syncShellHistory hook,
    //so we need to give it a bit of time to process that before we check this.. :/
    setTimeout(async () => {
      shell
        .invoke?.('window:history:can-go-back', undefined)
        .then((res) => {
          setCanGoBack(res);
        })
        .catch(() => setCanGoBack(false));
      shell
        .invoke?.('window:history:can-go-forward', undefined)
        .then((res) => {
          setCanGoForward(res);
        })
        .catch(() => setCanGoForward(false));
    }, 500);
  }, [legacyLoc?.current.href, loc?.href]);

  return {
    back,
    forward,
    canGoBack,
    canGoForward,
  };
};

/**
 * Syncs the location history to the shell. The shell actually keeps track of the history, and communicates it to the window when needed
 */
export const useSyncShellHistory = () => {
  useEffect(() => {
    if (shell.isShell) {
      shell.emit?.('window:history:clear', undefined);
    }
  }, []);
};
